import { DeleteOutlined } from '@mui/icons-material';
import { Chip, IconButton } from '@mui/material';
import type { CellContext, ColumnDef } from '@tanstack/react-table';
import { useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import type { GeoblockRule } from '@/models/Geoblock';
import { getLocationBreadcrumb, getLocationLabel } from '@/utils/countryUtils';

import VNTable from '../VNTable/VNTable';
import { GEOGRAPHY_LEVELS } from './GeoblockConstants';

type Row = {
  ruleIndex: number;
  effect: 'Allow' | 'Deny';
  type: string;
  value: string;
  onDelete: () => void;
};

const EffectCell = ({ row }: CellContext<Row, unknown>) => (
  <Chip
    label={row.original.effect === 'Allow' ? 'Allow' : 'Block'}
    size="small"
    color={row.original.effect === 'Allow' ? 'success' : 'error'}
    variant="filled"
  />
);

const LevelCell = ({ row }: CellContext<Row, unknown>) => GEOGRAPHY_LEVELS[row.original.type]?.singular || row.original.type;

const LocationCell = ({ row }: CellContext<Row, unknown>) => {
  const { value, type } = row.original;
  const breadcrumb = getLocationBreadcrumb(value);
  return (
    <div>
      <div>{getLocationLabel(value, type)}</div>
      {breadcrumb && <div className="location-breadcrumb">{breadcrumb}</div>}
    </div>
  );
};

const DeleteCell = ({ row }: CellContext<Row, unknown>) => (
  <IconButton size="small" onClick={row.original.onDelete}>
    <DeleteOutlined fontSize="small" />
  </IconButton>
);

const columns: ColumnDef<Row>[] = [
  {
    accessorKey: 'effect',
    header: 'Action',
    size: 100,
    cell: EffectCell,
    meta: { sortable: false },
  },
  {
    accessorKey: 'type',
    header: 'Level',
    cell: LevelCell,
    meta: { sortable: false },
  },
  {
    accessorKey: 'value',
    header: 'Location',
    cell: LocationCell,
    meta: { sortable: false },
  },
  {
    id: 'actions',
    header: 'Actions',
    size: 80,
    cell: DeleteCell,
    meta: { sortable: false },
  },
];

type Props = {
  name: string;
};

const GeoblockActiveRules = ({ name }: Props) => {
  const { watch, setValue } = useFormContext();
  const rules = watch(`${name}.rules`);

  const deleteAllRules = useCallback(() => {
    const cleared = (rules || []).map((rule: GeoblockRule) => ({
      ...rule,
      values: [] as string[],
    }));
    setValue(`${name}.rules`, cleared, { shouldDirty: true, shouldValidate: true });
  }, [rules, setValue, name]);

  const deleteRuleValue = useCallback(
    (ruleIndex: number, value: string) => {
      const current: string[] = [...(rules[ruleIndex]?.values || [])];
      const updated = current.filter((v) => v !== value);

      const newRules = JSON.parse(JSON.stringify(rules)); // deep copy to avoid direct state mutation
      newRules[ruleIndex].values = updated;

      setValue(`${name}.rules`, newRules, { shouldDirty: true, shouldValidate: true });
    },
    [rules, setValue, name]
  );

  const flatRows: Row[] = useMemo(
    () =>
      (rules || []).flatMap((rule: GeoblockRule, ruleIndex: number) =>
        rule.values.map((value: string) => ({
          ruleIndex,
          effect: rule.effect,
          type: rule.type,
          value,
          onDelete: () => deleteRuleValue(ruleIndex, value),
        }))
      ),
    [rules, deleteRuleValue]
  );

  return (
    <div>
      <div className="active-rules-section">
        <div className="active-rules-header">
          <div className="active-rules-title">Active Rules ({flatRows.length})</div>
          {flatRows.length > 0 && (
            <button type="button" className="browser-toggle" onClick={deleteAllRules}>
              <DeleteOutlined fontSize="small" />
              Delete All
            </button>
          )}
        </div>
        <VNTable
          columns={columns}
          data={flatRows}
          hidePagination
          noDataMessage={<div className="no-rules">No rules created yet. Use the Rule Builder to add your first rule.</div>}
        />
        {/* <GeoblockTestLocation name={name} /> */}
      </div>
    </div>
  );
};

export default GeoblockActiveRules;
